import React, {useEffect, useMemo, useState} from "react";
import {format} from "date-fns";
import {FaExclamationTriangle, FaExternalLinkAlt, FaInfoCircle, FaPlus, FaSpinner} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {MaskDefault} from "../../../../../utils/mask.js";
import AttendanceService from "../../../../../services/dispatch/attendance-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import AlertMessages from "../../../../../components/messages/alert-message.js";
import AttendanceProcedureModal from "./attendance-procedure-modal.jsx";
import ReviseTextButton from "../../../../../components/revise-text/revise-text-button.jsx";

const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";
const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB.replace(/^\//, "");

const AttendanceDataCard = ({
    id,
    form,
    optionsData,
    dispatch,
    requesterAlert,
    canCreateJanitorial,
    canSearchCortex,
    onChangeField,
    onChangeLinkedAttendance,
    onChangeContactAddress,
    onOpenRequesterModal,
    setIsLoading
}) => {
    const navigate = navigateHook();
    const [procedureModal, setProcedureModal] = useState({open: false, content: ""});

    const statusOptions = useMemo(() => {
        if (!form.attendanceLinked) return optionsData.status;
        if (optionsData.status.some(item => item.value === "VINCULADO")) return optionsData.status;
        return [...optionsData.status, {value: "VINCULADO", label: "VINCULADO", closedAttendance: true}];
    }, [form.attendanceLinked, optionsData.status]);

    const selectedNature = form.nature;
    const hasProcedure = !!selectedNature?.attendanceProcedure;
    const isNatureJanitorial = !!selectedNature?.janitorial;
    const janitorialButton = form.janitorialId
        ? {mode: "view", id: form.janitorialId}
        : {mode: "create", enabled: !!id && isNatureJanitorial && canCreateJanitorial};

    const lastDispatch = dispatch?.id
        ? {...dispatch, url: `${API_DESPACHO_WEB}/dispatch/form/${dispatch.id}`}
        : null;

    const openNotification = (value) => {
        AttendanceService.isNotificationSigned(value)
            .then((signed) => {
                const target = signed ? "notifications-signed" : "notifications-open";
                navigate(`/${target}?id=${value}`, true);
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível abrir a notificação.", "Ok");
            });
    };

    const extraInfoLinks = [
        form.sosId ? {
            key: "sos",
            onClick: () => navigate(`/sos/event-open/${form.sosId}`, true),
        } : null,
        form.notificationId ? {
            key: "notification",
            onClick: () => openNotification(form.notificationId),
        } : null,
        form.individualAlarmId ? {
            key: "individual-alarm",
            onClick: () => navigate(`/individual-alarm?lastAlarmId=${form.individualAlarmId}`, true),
        } : null,
    ].filter(Boolean);

    const onOpenLinkedAttendance = () => {
        const attendanceId = form.attendanceLinked.id ?? form.attendanceLinked.value;
        const urlDev = `/${API_DESPACHO_WEB}/attendance/form/${attendanceId}`;

        navigate(urlDev, true);
    };

    const optionValue = (option) => {
        if (option == null || option === "") return "";
        if (typeof option === "object") return option.value ?? "";
        return option;
    };

    const loadProcedure = () => {
        if (!form.nature?.natureId) return;

        AttendanceService.getAttendanceProcedure(form.nature.natureId)
            .then((content) => setProcedureModal({open: true, content}))
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível carregar o modelo.", "Ok");
            });
    };

    const changeStatus = (option) => {
        onChangeField("statusName", option);

        const isClosed = option?.closedAttendance !== false && String(option?.closedAttendance) !== "false";
        if (!isClosed) {
            onChangeField("end", "");
            onChangeField("showEnd", false);
            return;
        }

        AttendanceService.getDateFormattedView()
            .then((date) => {
                onChangeField("end", date);
                onChangeField("showEnd", true);
            })
            .catch(() => {
                onChangeField("end", format(new Date(), "dd/MM/yyyy HH:mm"));
                onChangeField("showEnd", true);
            });
    };

    const copyProcedure = () => {
        const transcription = [form.transcription, procedureModal.content].filter(Boolean).join(form.transcription ? "\n\n" : "");

        onChangeField("transcription", transcription);
        setProcedureModal({open: false, content: ""});
    };

    const openJanitorial = (janitorialId) => {
        navigate(`/janitorial/form/${janitorialId}`, true)
    }

    const createJanitorial = () => {
        projectUtils.swalMessage("info", "", "Gerar zeladoria?", "Sim", true, "Não", ({isConfirmed}) => {
            if (!isConfirmed) return;

            AttendanceService.createJanitorial({attendanceId: id, nature: optionValue(form.nature)})
                .then(({janitorialId}) => {
                    onChangeField("janitorialId", janitorialId);
                    openJanitorial(janitorialId);
                })
                .catch((error) => {
                    console.error(error);
                    projectUtils.swalErrorMessage("", "Não foi possível cadastrar zeladoria.", "Ok");
                });
        });
    };

    const searchCortex = () => {
        const cpf = String(form.contactCpf ?? "").replace(/[^0-9]+/g, "");
        if (!cpf) {
            projectUtils.swalMessage("warning", "", "Informe o CPF para realizar a pesquisa.", "Ok");
            return;
        }

        AttendanceService.personDataByCpf(cpf)
            .then((data) => {
                if (data?.name) onChangeField("contactName", data.name);
                if (data?.phone) onChangeField("contactPhone", data.phone);

                if (data?.address) {
                    onChangeContactAddress(prev => ({
                        ...prev,
                        street: data.address.address ?? prev.street,
                        neighborhood: data.address.neighborhood ?? prev.neighborhood,
                        number: data.address.number ?? prev.number,
                        city: data.address.city ?? prev.city,
                    }));
                }
            })
            .catch((error) => {
                console.error(error);

                const message = error.response?.data?.message;
                const errMessage = AlertMessages[message] ?? "Ocorreu um erro na requisição de dados do indivíduo.";

                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    };

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Dados</Card.Title>
                </Card.Header>
                <Card.Body className="flex flex-col gap-4">
                    <SentryRow className="items-end">
                        <InputDatetime
                            classNameWrapper="col-span-12 md:col-span-3"
                            inputClass={REQUIRED_BG}
                            labelClass={REQUIRED_LABEl_BG}
                            id="start"
                            label="Data/Hora inicial *"
                            dateFormat="LL"
                            returnDateObj={false}
                            selected={form.start}
                            onChange={(value) => onChangeField("start", value ?? "")}
                        />
                        {form.showEnd && (
                            <InputDatetime
                                classNameWrapper="col-span-12 md:col-span-3"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="end"
                                label="Data/Hora final *"
                                dateFormat="LL"
                                returnDateObj={false}
                                selected={form.end}
                                disabled={form.linkedLocked}
                                onChange={(value) => onChangeField("end", value ?? "")}
                            />
                        )}

                        {!form.showEnd && (<div className={"col-span-3"}></div>)}
                        {lastDispatch?.id && (
                            <div className="col-span-12 md:col-span-6 text-sm text-[#d63737] flex items-center justify-end">
                               (Último despacho criado em: {projectUtils.formatDate(lastDispatch.start)})
                                <button
                                    type="button"
                                    className="ml-2 text-blue-700 cursor-pointer"
                                    title="Abrir despacho"
                                    onClick={() => {
                                        navigate(lastDispatch.url, true)
                                    }}
                                >
                                    <FaExternalLinkAlt/>
                                </button>
                            </div>
                        )}
                    </SentryRow>

                    <SentryRow className="items-end border-t border-gray-200 pt-4">
                        <div className="col-span-12 md:col-span-4 flex gap-2">
                            <SelectComponent
                                classNameWrapper="flex-1"
                                id="attendanceLinked"
                                label="Atendimento vinculado"
                                options={optionsData.attendances}
                                value={form.attendanceLinked}
                                onChange={onChangeLinkedAttendance}
                            />
                            <Button
                                target="primary-outline"
                                title="Abrir atendimento vinculado"
                                disabled={!form.attendanceLinked}
                                onClick={onOpenLinkedAttendance}
                            >
                                <FaExternalLinkAlt/>
                            </Button>
                        </div>
                        <InputForm classNameWrapper="col-span-12 md:col-span-2" id="protocol" label="Nº protocolo" value={form.protocol} disabled/>
                        <InputForm classNameWrapper="col-span-12 md:col-span-3" id="userCreated" label="Cadastrado por" value={form.userCreated} disabled/>
                        <InputForm classNameWrapper="col-span-12 md:col-span-3" id="lastUser" label="Última alteração" value={form.lastUser} disabled/>
                    </SentryRow>

                    <SentryRow className="items-end">
                        <SelectComponent
                            classNameWrapper="col-span-12 lg:col-span-4"
                            id="nature"
                            label="Natureza *"
                            options={optionsData.natures}
                            value={form.nature}
                            isDisabled={form.linkedLocked}
                            onChange={(option) => onChangeField("nature", option)}
                        />
                        <div className="col-span-12 lg:col-span-2 grid grid-cols-2 gap-2">
                            <Button disabled={!hasProcedure} onClick={loadProcedure}>Modelo</Button>
                            {janitorialButton?.mode === "view" ? (
                                <Button onClick={() => openJanitorial(janitorialButton.id)}>
                                    <FaExternalLinkAlt/> Zeladoria
                                </Button>
                            ) : (
                                <Button disabled={!janitorialButton?.enabled} onClick={createJanitorial}>
                                    <FaPlus/> Zeladoria
                                </Button>
                            )}
                        </div>
                        <SelectComponent
                            classNameWrapper="col-span-12 md:col-span-2"
                            id="priority"
                            label="Prioridade"
                            options={optionsData.priorities}
                            value={form.priority}
                            isDisabled={form.linkedLocked}
                            onChange={(option) => onChangeField("priority", option)}
                        />
                        <SelectComponent
                            classNameWrapper="col-span-12 md:col-span-2"
                            id="statusName"
                            label="Status *"
                            options={statusOptions}
                            value={form.statusName}
                            isDisabled={!!form.attendanceLinked}
                            onChange={changeStatus}
                        />
                        <SelectComponent
                            classNameWrapper="col-span-12 md:col-span-2"
                            id="channel"
                            label="Canal *"
                            options={optionsData.channels}
                            value={form.channel}
                            onChange={(option) => onChangeField("channel", option)}
                        />
                    </SentryRow>

                    <div className="border-t border-gray-200 pt-4">
                        <h4 className="mb-3 text-base font-semibold text-gray-600">Informações do solicitante</h4>
                        <SentryRow className="items-end">
                            <InputForm
                                classNameWrapper="col-span-12 md:col-span-3"
                                id="contactName"
                                label="Nome"
                                value={form.contactName}
                                onChange={({target}) => onChangeField("contactName", target.value)}
                            />

                            <InputForm
                                classNameWrapper="col-span-12 md:col-span-3"
                                id="contactPhone"
                                label="Telefone"
                                value={form.contactPhone || ""}
                                onChange={({target}) => onChangeField("contactPhone", target.value)}
                                onBlur={() => onChangeField("contactPhone", form.contactPhone, {refreshRequester: true})}
                                mask={MaskDefault.phone}
                            />
                            <div className="col-span-12 md:col-span-3 flex gap-2">
                                <InputForm
                                    classNameWrapper="flex-1"
                                    id="contactCpf"
                                    label="CPF"
                                    value={form.contactCpf}
                                    onChange={({target}) => onChangeField("contactCpf", target.value)}
                                    onBlur={() => onChangeField("contactCpf", form.contactCpf, {refreshRequester: true})}
                                    mask={MaskDefault.cpf}
                                />
                                <Button
                                    className={"w-9 p-1!"}
                                    target="primary-outline"
                                    title="Pesquisar no Córtex"
                                    disabled={!canSearchCortex}
                                    onClick={searchCortex}
                                >
                                    {canSearchCortex && <img className={"w-full"} src={getAssetPath("/img/cortex-enabled.svg")} alt=""/>}
                                    {!canSearchCortex && <img className={"w-full"} src={getAssetPath("/img/cortex-disabled.svg")} alt=""/>}
                                </Button>
                            </div>
                            <div className="col-span-6 md:col-span-2 flex h-[32px] items-center">
                                <InputCheckbox
                                    id="anonymous"
                                    label="Anônimo"
                                    checked={form.anonymous}
                                    onChange={({target}) => onChangeField("anonymous", target.checked)}
                                />
                            </div>
                            <div className="col-span-6 md:col-span-1 flex justify-end">
                                <Button
                                    target={requesterAlert.alert ? "danger" : "primary"}
                                    title={requesterAlert.message}
                                    disabled={!requesterAlert.alert}
                                    onClick={onOpenRequesterModal}
                                >
                                    {requesterAlert.alert ? <FaExclamationTriangle/> : <FaInfoCircle/>}
                                </Button>
                            </div>
                        </SentryRow>
                    </div>

                    <div>
                        <InputTextarea
                            id="transcription"
                            label="Transcrição *"
                            value={form.transcription}
                            textAreaClass={REQUIRED_BG}
                            height={"200px"}
                            labelClass={REQUIRED_LABEl_BG}
                            onChange={({target}) => onChangeField("transcription", target.value)}
                        />

                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                            <ReviseTextButton
                                value={form.transcription}
                                setValue={(newValue) => onChangeField("transcription", newValue)}
                                module="Transcrição"
                                setIsLoading={setIsLoading}
                            />

                            {extraInfoLinks.map((link) => (
                                <button
                                    key={link.key}
                                    type="button"
                                    className="text-blue-700"
                                    onClick={link.onClick}
                                >
                                    Mais informações <FaExternalLinkAlt className="inline"/>
                                </button>
                            ))}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <AttendanceProcedureModal
                open={procedureModal.open}
                content={procedureModal.content}
                onOpenChange={(open) => setProcedureModal(prev => ({...prev, open}))}
                onCopy={copyProcedure}
            />
        </>
    );
};

export default AttendanceDataCard;
